<template>
  <div class="configurator-setup-modal">
    <!-- Basis informatie -->
    <div class="form-section">
      <div class="form-row">
        <TextInput
          :id="inputs.name.id"
          :label="inputs.name.label"
          v-model="inputs.name.binding"
          :error="inputs.name.error"
          :rules="inputs.name.rules"
          :placeholder="inputs.name.placeholder"
          @validate="onValidate('name', $event)"
        />
        <TextAreaInput
          :id="inputs.description.id"
          :label="inputs.description.label"
          v-model="inputs.description.binding"
          :error="inputs.description.error"
          :rules="inputs.description.rules"
          :placeholder="inputs.description.placeholder"
          @validate="onValidate('description', $event)"
        />
      </div>

      <!-- Settings -->
      <div class="form-row">
        <ToggleInput
          id="show_pricing"
          label="Toon prijzen tijdens configuratie"
          v-model="designSettings.showPricing"
        />
        <ToggleInput
          id="advanced_pricing"
          label="Geavanceerde prijsberekeningen"
          v-model="designSettings.advancedPricing"
        />
      </div>

      <!-- Advanced Pricing Options -->
      <div v-if="designSettings.advancedPricing" class="advanced-pricing-section">
        <h4>Pricing Configuratie</h4>
        <p class="section-description">
          Configureer geavanceerde prijsregels zoals matrix pricing, staffels en bundels.
        </p>

        <div class="pricing-options">
          <CheckboxInput
            id="enable_matrix_pricing"
            label="Matrix Pricing (B×H berekeningen)"
            v-model="pricingOptions.matrixPricing"
          />
          <CheckboxInput
            id="enable_tiered_pricing"
            label="Staffel Pricing (volume kortingen)"
            v-model="pricingOptions.tieredPricing"
          />
          <CheckboxInput
            id="enable_bundle_pricing"
            label="Bundle Pricing (combinatie kortingen)"
            v-model="pricingOptions.bundlePricing"
          />
          <CheckboxInput
            id="enable_formula_pricing"
            label="Formule-based Pricing"
            v-model="pricingOptions.formulaPricing"
          />
        </div>
      </div>
    </div>

    <!-- Product selectie -->
    <div class="form-section">
      <h4>Productselectie</h4>
      <p class="section-description">
        Kies de producten die beschikbaar moeten zijn in deze configurator. Het formulier wordt
        automatisch gegenereerd op basis van de product opties.
      </p>

      <div class="product-selection">
        <div v-if="loading" class="loading-state">
          <LoadingSpinner />
          <span>Producten laden...</span>
        </div>

        <EmptyState
          v-else-if="!availableProducts.length"
          title="Geen producten beschikbaar"
          description="Maak eerst producten aan voordat je een configurator kunt maken."
          icon="box"
        >
          <Btn
            text="Naar Producten"
            variant="secondary"
            @click="$router.push('/products')"
            type="button"
          />
        </EmptyState>

        <div v-else class="product-grid">
          <div
            v-for="product in availableProducts"
            :key="product.id"
            class="product-card"
            :class="{ 'product-card--selected': isProductSelected(product.id) }"
            @click="toggleProduct(product.id)"
          >
            <div class="product-card__image">
              <img
                v-if="product.imageUrl"
                :src="product.imageUrl"
                :alt="product.name"
                @error="handleImageError"
              />
              <div v-else class="product-card__placeholder">
                <BaseIcon name="image" size="24" />
              </div>
            </div>

            <div class="product-card__content">
              <h4>{{ product.title }}</h4>
              <p v-if="product.description" class="product-description">
                {{ product.description }}
              </p>
              <div class="product-meta">
                <span class="product-options">{{ product.options?.length || 0 }} opties</span>
              </div>
            </div>

            <div class="product-card__checkbox">
              <CheckboxInput
                :id="`product_${product.id}`"
                :model-value="isProductSelected(product.id)"
                @click.stop
                @update:model-value="toggleProduct(product.id)"
              />
            </div>
          </div>
        </div>

        <div v-if="selectedProducts.length" class="selected-summary">
          <strong>{{ selectedProducts.length }} product(en) geselecteerd</strong>
        </div>
      </div>
    </div>

    <!-- Design instellingen section removed - moved to top -->

    <!-- Modal actions -->
    <div class="modal__actions">
      <Btn variant="secondary" @click="handleCancel" type="button">Annuleren</Btn>
      <Btn
        variant="primary"
        @click="handleSubmit"
        type="button"
        :disabled="!selectedProducts.length"
      >
        Configurator Aanmaken
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useValidation } from '@/composables/useValidation';
  import { useFormGeneration } from '@/composables/useFormGeneration';
  import { getProductService } from '@/composables/useProductService';
  import { useDesignStore } from '@/store/designStore';

  // Input components
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  // UI components
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';

  export default {
    name: 'ConfiguratorSetupModal',

    components: {
      TextInput,
      TextAreaInput,
      CheckboxInput,
      ToggleInput,
      Btn,
      BaseIcon,
      EmptyState,
      LoadingSpinner,
    },

    emits: ['submit', 'cancel'],

    setup(props, { emit }) {
      const router = useRouter();
      const { generateConfiguratorForm } = useFormGeneration();
      const loading = ref(true);
      const availableProducts = ref([]);
      const selectedProducts = ref([]);
      const productService = ref(null);

      // Validation setup
      const { inputs, validateForm, onValidate } = useValidation({
        name: {
          id: 'configurator_name',
          label: 'Configurator Naam',
          binding: '',
          error: '',
          rules: ['required', 'maxLength:100'],
          placeholder: 'bijv. Raam Configurator, Deur Samensteller',
        },
        description: {
          id: 'configurator_description',
          label: 'Beschrijving',
          binding: '',
          error: '',
          rules: ['maxLength:500'],
          placeholder: 'Beschrijf waar deze configurator voor gebruikt wordt...',
        },
      });

      // Design settings
      const designSettings = ref({
        showPricing: true,
        advancedPricing: false,
        // showImages: always true for better UX
        // autoGenerate: always true for runtime configurators
        // enableUpsells: feature for later
      });

      // Pricing options
      const pricingOptions = ref({
        matrixPricing: false,
        tieredPricing: false,
        bundlePricing: false,
        formulaPricing: false,
      });

      // Initialize service and load products
      const loadProducts = async () => {
        try {
          loading.value = true;
          if (!productService.value) {
            productService.value = await getProductService();
          }
          const products = await productService.value.getAllProducts();
          availableProducts.value = products || [];
        } catch (error) {
          console.error('Failed to load products:', error);
          availableProducts.value = [];
        } finally {
          loading.value = false;
        }
      };

      // Product selection methods
      const isProductSelected = (productId) => {
        return selectedProducts.value.includes(productId);
      };

      const toggleProduct = (productId) => {
        const index = selectedProducts.value.indexOf(productId);
        if (index > -1) {
          selectedProducts.value.splice(index, 1);
        } else {
          selectedProducts.value.push(productId);
        }
      };

      const handleImageError = (event) => {
        event.target.style.display = 'none';
      };

      // Form submission
      const handleSubmit = async () => {
        // Validate form first
        const isValid = await validateForm();
        if (!isValid) {
          return;
        }

        if (!selectedProducts.value.length) {
          return;
        }

        try {
          // Voor runtime configurators: alleen metadata opslaan
          const runtimeConfiguratorData = {
            flow_title: inputs.value.name.binding,
            description: inputs.value.description.binding,
            type: 'configurator',
            settings: {
              type: 'configurator',
              cpq: {
                isConfigurator: true,
                selectedProducts: selectedProducts.value,
                showPricing: designSettings.value.showPricing,
                designSettings: {
                  showImages: true, // Always true for better UX
                  enableUpsells: false, // Feature for later
                  advancedPricing: designSettings.value.advancedPricing,
                },
                pricingOptions: pricingOptions.value,
                // Runtime generation metadata
                runtimeGeneration: true,
                createdAt: new Date().toISOString(),
                version: '2.0',
              },
            },
            // Minimale pages - alleen metadata
            pages: [],
          };

          emit('submit', runtimeConfiguratorData);
        } catch (error) {
          console.error('Failed to create runtime configurator:', error);
          alert('Er is een fout opgetreden bij het aanmaken van de configurator');
        }
      };

      const handleCancel = () => {
        emit('cancel');
      };

      onMounted(loadProducts);

      return {
        // Validation
        inputs,
        onValidate,
        // Data
        loading,
        availableProducts,
        selectedProducts,
        designSettings,
        pricingOptions,
        // Computed
        // Methods
        isProductSelected,
        toggleProduct,
        handleImageError,
        handleSubmit,
        handleCancel,
        // Router
        router,
      };
    },
  };
</script>

<style scoped lang="scss">
  .configurator-setup-modal {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-height: 80vh;
    overflow-y: auto;
  }

  .form-section {
    .section-description {
      margin: 0 0 1.5rem 0;
      color: var(--slate-600);
      font-size: 0.875rem;
      line-height: 1.5;
    }
  }

  .form-toggles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .product-selection {
    .loading-state {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 2rem;
      justify-content: center;
      color: var(--slate-600);
    }
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .product-card {
    border: 2px solid var(--slate-200);
    border-radius: 8px;
    padding: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 1rem;

    &:hover {
      border-color: var(--brand-300);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &--selected {
      border-color: var(--brand-500);
      background: var(--brand-50);
    }

    &__image {
      width: 64px;
      height: 64px;
      flex-shrink: 0;
      border-radius: 6px;
      overflow: hidden;
      margin-bottom: 0.75rem;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__placeholder {
      color: var(--slate-400);
    }

    &__content {
      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--slate-800);
      }

      .product-description {
        margin: 0 0 0.75rem 0;
        font-size: 0.875rem;
        color: var(--slate-600);
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    &__meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;

      .product-price {
        font-weight: 600;
        color: var(--brand-600);
      }

      .product-options {
        color: var(--slate-500);
      }
    }

    &__checkbox {
      position: absolute;
      top: 0.125rem;
      right: 0.125rem;
    }
  }

  .selected-summary {
    padding: 0.75rem 1rem;
    background: var(--brand-50);
    border: 1px solid var(--brand-200);
    border-radius: 6px;
    color: var(--brand-700);
    text-align: center;
  }

  .advanced-pricing-section {
    margin-top: 1.5rem;
    padding: 1rem;
    background: var(--slate-50);
    border-radius: 8px;
    border: 1px solid var(--slate-200);

    h4 {
      margin: 0 0 0.5rem 0;
      color: var(--slate-900);
      font-weight: 600;
    }

    .pricing-options {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
      margin-top: 1rem;
    }
  }

  .modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
    margin-top: 1rem;
  }
</style>
